import { useAppSettings } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { ClinicProfileCard } from "@/features/settings/ClinicProfileCard";
import { ClinicDoctorsSection } from "@/features/settings/ClinicDoctorsSection";
import { ClinicalCatalogsCard } from "@/features/settings/ClinicalCatalogsCard";
import { AiCard } from "@/features/settings/AiCard";
import { AiPromptsCard } from "@/features/settings/AiPromptsCard";
import { WebhooksFlowsSection, WhatsappRemindersSection } from "@/features/settings/WebhooksCard";
import { R2Card } from "@/features/settings/R2Card";
import { SlotConfigCard } from "@/features/settings/SlotConfigCard";
import { WeeklyScheduleEditor } from "@/features/settings/WeeklyScheduleEditor";
import { BlockDaysCard } from "@/features/settings/BlockDaysCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Has } from "@/components/Has";

export default function SettingsPage() {
  const { isAdmin, permissions } = useAuth();
  const { data: settings, isLoading } = useAppSettings();

  // Direct-URL guard (2026-10-15): the sidebar only renders the Settings link
  // for settings.view holders, but the route itself was reachable by URL —
  // a user without the permission hit the gated GET /settings (403) and saw
  // an infinite skeleton (isLoading never resolves to data). Render a clear
  // forbidden state instead; no gated fetch fires for them.
  const canView = isAdmin || permissions.has("settings.view");
  if (!canView) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Settings</h1>
        <p className="mt-4 text-sm text-muted">You do not have permission to view clinic settings.</p>
      </div>
    );
  }
  if (isLoading || !settings) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <Skeleton className="h-10 w-48" />
        <div className="mt-4 space-y-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }
  const canEditClinic = isAdmin || permissions.has("settings.clinic");
  const canEditAi = isAdmin || permissions.has("settings.ai");
  const canEditWebhooks = isAdmin || permissions.has("settings.webhooks");
  const canEditR2 = isAdmin || permissions.has("settings.r2");
  const canEditScheduling = isAdmin || permissions.has("scheduling.manage");

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Settings</h1>
      {/* 7 tabs (2026-10-18): the admin-only AI Chat tab was removed — the
          apply wizard's Auto-fill + Refine with AI replaced the standalone
          scheduling-assistant chat. Uncontrolled Tabs (nothing switches
          tabs programmatically anymore). */}
      <Tabs defaultValue="clinic" className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="clinic">Clinic Profile</TabsTrigger>
          <TabsTrigger value="doctors">Doctors</TabsTrigger>
          <TabsTrigger value="catalogs">Clinic Catalogs</TabsTrigger>
          <TabsTrigger value="scheduling">Slots</TabsTrigger>
          <TabsTrigger value="blockdays">Block Days</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="ai-prompts">AI Prompts</TabsTrigger>
        </TabsList>
        <TabsContent value="clinic" className="space-y-4">
          <ClinicProfileCard settings={settings} canEdit={canEditClinic} />
        </TabsContent>
        <TabsContent value="doctors" className="space-y-4">
          <ClinicDoctorsSection canEdit={canEditClinic} />
        </TabsContent>
        <TabsContent value="catalogs" className="space-y-4">
          <ClinicalCatalogsCard />
        </TabsContent>
        <TabsContent value="scheduling" className="space-y-4">
          <Has perm="scheduling.manage">
            <SlotConfigCard
              settings={settings}
              canEdit={canEditScheduling}
              isAdmin={isAdmin}
            />
          </Has>
          <Has perm="settings.clinic">
            <WeeklyScheduleEditor schedule={settings.weeklySchedule} />
          </Has>
        </TabsContent>
        <TabsContent value="blockdays" className="space-y-4">
          <Has perm="scheduling.manage">
            <BlockDaysCard canEdit={canEditScheduling} />
          </Has>
        </TabsContent>
        {/* Integrations tab is INTERNALLY TABBED (2026-10-18): OpenRouter /
            Cloudflare R2 / Webhooks / WhatsApp. Uncontrolled local Tabs —
            nothing needs to switch sub-tabs programmatically; default
            'OpenRouter'. Per-card canEdit gating unchanged (canEditAi /
            canEditR2 / canEditWebhooks). */}
        <TabsContent value="integrations" className="space-y-4">
          <Tabs defaultValue="openrouter">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="openrouter">OpenRouter</TabsTrigger>
              <TabsTrigger value="r2">Cloudflare R2</TabsTrigger>
              <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
              <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            </TabsList>
            <TabsContent value="openrouter" className="mt-4">
              <AiCard settings={settings} canEdit={canEditAi} />
            </TabsContent>
            <TabsContent value="r2" className="mt-4">
              <R2Card settings={settings} canEdit={canEditR2} />
            </TabsContent>
            <TabsContent value="webhooks" className="mt-4">
              <WebhooksFlowsSection settings={settings} canEdit={canEditWebhooks} />
            </TabsContent>
            <TabsContent value="whatsapp" className="mt-4">
              <WhatsappRemindersSection settings={settings} canEdit={canEditWebhooks} />
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="ai-prompts" className="space-y-4">
          <AiPromptsCard settings={settings} canEdit={canEditAi} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
